import { Alert, Button, Grid, TextField, Typography } from '@mui/material';
import { LabExternalStorageProviderCreate, LabExternalStorageProviderCredentials } from 'interfaces/externalStorage';
import React, { FormEvent, useState } from 'react';

interface ProviderCredentialsFormProps {
  isSaving: boolean;
  mode: 'create' | 'update';
  providerName?: string;
  submitError?: string;
  onCancel: () => void;
  onSubmit: (value: LabExternalStorageProviderCreate) => void;
}

const EMPTY_CREDENTIALS: LabExternalStorageProviderCredentials = {
  accessKeyId: '',
  secretAccessKey: '',
  region: '',
  provider: 'AWS',
  envAuth: 'false',
};

export const ProviderCredentialsForm: React.FC<ProviderCredentialsFormProps> = ({
  isSaving,
  mode,
  providerName = '',
  submitError,
  onCancel,
  onSubmit,
}) => {
  const [name, setName] = useState(providerName);
  const [credentials, setCredentials] = useState(EMPTY_CREDENTIALS);
  const trimmedName = name.trim();
  const isValid =
    Boolean(trimmedName) &&
    Boolean(credentials.accessKeyId.trim()) &&
    Boolean(credentials.secretAccessKey.trim()) &&
    Boolean(credentials.region.trim());

  const setCredential = (key: keyof LabExternalStorageProviderCredentials, value: string) => {
    setCredentials((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!isValid) {
      return;
    }

    onSubmit({
      name: trimmedName,
      providerType: 's3',
      credentials: {
        ...credentials,
        accessKeyId: credentials.accessKeyId.trim(),
        secretAccessKey: credentials.secretAccessKey.trim(),
        region: credentials.region.trim(),
        ...(credentials.serverSideEncryption?.trim()
          ? { serverSideEncryption: credentials.serverSideEncryption.trim() }
          : {}),
      },
    });
  };

  return (
    <Grid component="form" container spacing={2} onSubmit={handleSubmit} aria-label={`${mode} provider credentials`}>
      <Grid item xs={12}>
        <Typography variant="h6">
          {mode === 'create' ? 'Add provider' : `Update ${providerName} credentials`}
        </Typography>
      </Grid>
      {submitError ? (
        <Grid item xs={12}>
          <Alert severity="error">{submitError}</Alert>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <TextField
          label="Provider name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          fullWidth
          disabled={mode === 'update' || isSaving}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Access key ID"
          value={credentials.accessKeyId}
          onChange={(event) => setCredential('accessKeyId', event.target.value)}
          required
          fullWidth
          disabled={isSaving}
          autoComplete="off"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Secret access key"
          type="password"
          value={credentials.secretAccessKey}
          onChange={(event) => setCredential('secretAccessKey', event.target.value)}
          required
          fullWidth
          disabled={isSaving}
          autoComplete="new-password"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Region"
          value={credentials.region}
          onChange={(event) => setCredential('region', event.target.value)}
          placeholder="us-east-2"
          required
          fullWidth
          disabled={isSaving}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Server-side encryption"
          value={credentials.serverSideEncryption || ''}
          onChange={(event) => setCredential('serverSideEncryption', event.target.value)}
          placeholder="Optional, for example AES256"
          fullWidth
          disabled={isSaving}
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container justifyContent="flex-end" spacing={1}>
          <Grid item>
            <Button onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button type="submit" variant="contained" disabled={!isValid || isSaving}>
              {isSaving ? 'Saving…' : mode === 'create' ? 'Add provider' : 'Update credentials'}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
